import React, { useState } from 'react';
import { Input, Button, Select, Space, message, Spin, Upload, Divider, Typography } from 'antd';
import { ThunderboltOutlined, FileImageOutlined, CloudUploadOutlined, ExperimentOutlined } from '@ant-design/icons';
import ImgCrop from 'antd-img-crop';
import axiosHttp from '../../../appRedux/services/axiosCommon';

const { TextArea } = Input;
const { Dragger } = Upload;
const { Title, Text } = Typography;

const MODELS = [
  { label: 'FLUX.1 Schnell (Fast)', value: '@cf/black-forest-labs/flux-1-schnell' },
  { label: 'FLUX.1 Dev (Quality)', value: '@cf/black-forest-labs/flux-1-dev' },
];

const PROMPT_PRESETS = [
  {
    label: 'Vibrant Gradient',
    prompt: 'A clean abstract gradient background transitioning from bright orange to yellow. Minimalist, high resolution, soft lighting, modern design.'
  },
  {
    label: 'Dark Glassmorphism',
    prompt: 'A dark mode abstract background with deep navy blue and purple colors. Glowing neon accents, frosted glass effect, minimalist geometric shapes, tech aesthetic.'
  },
  {
    label: 'Soft Pastel',
    prompt: 'A calming pastel background featuring light pink and lavender colors. Flowing shapes, airy atmosphere, perfect for typography overlay.'
  },
  {
    label: 'Professional',
    prompt: 'A clean abstract background in shades of light grey and blue. Geometric patterns, sleek design, bright lighting, uncluttered.'
  }
];

export default function GenerateTab({ onSelectImage, activeWidth, activeHeight }) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [selectedModel, setSelectedModel] = useState(MODELS[0].value);

  const targetAspect = (activeWidth && activeHeight) ? (activeWidth / activeHeight) : (1200 / 400);

  // Handle local file upload (intercept and load into canvas immediately)
  const handleBeforeUpload = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Pass the cropped local file directly to the studio editor
        onSelectImage(reader.result, activeWidth, activeHeight);
        reject(); // Stop default antd upload behavior
      };
      reader.onerror = () => {
        message.error('Failed to read local file.');
        reject();
      };
    });
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      message.error('Please enter a description prompt first.');
      return;
    }

    setLoading(true);
    setGeneratedImage(null);

    try {
      const response = await axiosHttp.post('/v2/ai/image/generate', {
        prompt: prompt.trim(),
        width: activeWidth || 1200,
        height: activeHeight || 628,
        model: selectedModel,
      });

      if (response.data && response.data.status && response.data.results?.image) {
        let imgSource = response.data.results.image;
        if (imgSource && imgSource.startsWith('eyJyZXN1bHQi')) {
          try {
            const decodedStr = window.atob(imgSource);
            const parsed = JSON.parse(decodedStr);
            if (parsed && parsed.result && parsed.result.image) {
              imgSource = parsed.result.image;
            }
          } catch (e) {
            console.error("Failed to decode double-encoded image string:", e);
          }
        }

        const formattedSource = (imgSource.startsWith('data:') || imgSource.startsWith('http://') || imgSource.startsWith('https://'))
          ? imgSource 
          : `data:image/png;base64,${imgSource}`;
        
        setGeneratedImage(formattedSource);
        message.success('Background generated successfully!');
      } else {
        message.error(response.data?.message || 'Failed to generate background image.');
      }
    } catch (err) {
      console.error('Image Generation Error:', err);
      message.error('An error occurred while generating the image.');
    } finally {
      setLoading(false);
    }
  };

  const handleUseBackground = () => {
    if (generatedImage) {
      onSelectImage(generatedImage, activeWidth, activeHeight);
    }
  };

  return (
    <div className="generate-tab-container" style={{ display: 'flex', flexDirection: 'row', gap: '32px', padding: '24px', height: '100%', background: '#f8fafc', borderRadius: '12px' }}>
      
      {/* Left Panel: Upload Zone */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', background: '#f1f5f9' }}>
          <Title level={5} style={{ margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CloudUploadOutlined style={{ color: '#3b82f6' }} /> Upload from Computer
          </Title>
          <Text type="secondary" style={{ fontSize: '13px' }}>Optimized and strictly cropped to match preset.</Text>
        </div>
        <div style={{ flex: 1, padding: '32px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <ImgCrop rotationSlider aspect={targetAspect} quality={1}>
            <Dragger
              name="image"
              beforeUpload={handleBeforeUpload}
              multiple={false}
              showUploadList={false}
              style={{
                background: '#fafafa',
                borderColor: '#d9d9d9',
                borderRadius: '12px',
                padding: '40px 20px',
              }}
            >
              <p className="ant-upload-drag-icon">
                <CloudUploadOutlined style={{ color: '#1890ff', fontSize: '48px' }} />
              </p>
              <p className="ant-upload-text" style={{ fontSize: '18px', fontWeight: 600, color: '#1e293b' }}>
                Click or drag image to this area
              </p>
              <p className="ant-upload-hint" style={{ fontSize: '14px', color: '#64748b', marginTop: '12px' }}>
                Image will automatically be cropped and sent to the Studio Editor. <br/>
                <b>Required Target Aspect: {activeWidth}:{activeHeight}</b>
              </p>
            </Dragger>
          </ImgCrop>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '1px', height: '80%', background: '#cbd5e1' }} />
      </div>

      {/* Right Panel: AI Generation */}
      <div style={{ flex: 1.2, display: 'flex', flexDirection: 'column', background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', background: '#f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={5} style={{ margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ExperimentOutlined style={{ color: '#8b5cf6' }} /> Generate with AI
            </Title>
            <Text type="secondary" style={{ fontSize: '13px' }}>Create an abstract or themed background.</Text>
          </div>
          <div style={{ background: '#e0e7ff', color: '#4338ca', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600 }}>
            {activeWidth || 1200} × {activeHeight || 628}
          </div>
        </div>
        
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, overflowY: 'auto' }}>
          {generatedImage ? (
             <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
               <div style={{ flex: 1, background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}>
                 <img src={generatedImage} alt="AI Generated Background" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '6px' }} />
               </div>
               <div style={{ display: 'flex', gap: '12px' }}>
                 <Button onClick={() => setGeneratedImage(null)} style={{ flex: 1 }}>Discard & Try Again</Button>
                 <Button type="primary" onClick={handleUseBackground} style={{ flex: 2, background: '#10b981', borderColor: '#10b981' }}>
                   Use in Editor
                 </Button>
               </div>
             </div>
          ) : (
            <>
              <div>
                <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155', display: 'block', marginBottom: '8px' }}>
                  Prompt / Background Description
                </label>
                <TextArea
                  rows={4}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g. A futuristic learning desk with abstract neon grids..."
                  disabled={loading}
                  style={{ borderRadius: '8px' }}
                />
                <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {PROMPT_PRESETS.map((preset) => (
                    <Button key={preset.label} size="small" onClick={() => setPrompt(preset.prompt)} disabled={loading} style={{ fontSize: '12px', borderRadius: '16px' }}>
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155', display: 'block', marginBottom: '8px' }}>
                  AI Model
                </label>
                <Select
                  value={selectedModel}
                  onChange={setSelectedModel}
                  options={MODELS}
                  style={{ width: '100%' }}
                  disabled={loading}
                />
              </div>

              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={handleGenerate}
                loading={loading}
                style={{
                  height: '44px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                  border: 'none',
                  fontWeight: 600,
                  marginTop: 'auto',
                }}
              >
                Generate Background
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
